import { differenceInCalendarMonths, parseISO, startOfMonth } from 'date-fns';
import { AlertCircle, Bike, CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/lib/formatters';
import { formatDate } from '@/lib/utils';
import { formatPlaca } from '@/utils/formatPlaca';
import type { DashboardCartaoLocacao } from '@/types/clienteMobile';

export interface ClienteVirtualLocacaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  nomeLocatario: string;
  placa?: string | null;
  marca?: string | null;
  modelo?: string | null;
  codigoCliente?: string | null;
  empresaNome?: string | null;
  vencidosQuantidade: number;
  vencidosValor: number;
  cartaoLocacao?: DashboardCartaoLocacao | null;
}

function mesesContratoEntre(inicioIso: string, fimRef: Date | string): number | null {
  try {
    const inicio = startOfMonth(parseISO(inicioIso.length === 10 ? `${inicioIso}T12:00:00` : inicioIso));
    const fim = startOfMonth(typeof fimRef === 'string' ? parseISO(fimRef.length === 10 ? `${fimRef}T12:00:00` : fimRef) : fimRef);
    const diff = differenceInCalendarMonths(fim, inicio);
    return Math.max(1, diff + 1);
  } catch {
    return null;
  }
}

function locacaoCancelada(c: DashboardCartaoLocacao | null | undefined): boolean {
  if (!c) return false;
  if (c.dataCancelamento) return true;
  const s = (c.statusLocacao ?? '').toUpperCase();
  return s.includes('CANCEL');
}

function linhaModelo(marca?: string | null, modelo?: string | null): string {
  return [marca?.trim(), modelo?.trim()].filter(Boolean).join(' ') || '—';
}

function textoModeloVeiculo(
  loc: DashboardCartaoLocacao | null | undefined,
  marca?: string | null,
  modelo?: string | null
): string {
  const fab = loc?.fabricanteEquipamento?.trim();
  const modEq = loc?.modeloEquipamento?.trim();
  let line = [fab, modEq].filter(Boolean).join(' ').trim();
  const anoM = loc?.anoModeloEquipamento?.trim();
  const anoF = loc?.anoFabricacaoEquipamento?.trim();
  if (line) {
    const anoPart = anoF && anoM && anoF !== anoM ? `${anoF}/${anoM}` : anoM || anoF || '';
    if (anoPart && !line.includes(anoPart)) line = `${line} ${anoPart}`.trim();
  }
  if (line) return line;
  return linhaModelo(marca, modelo);
}

export function ClienteVirtualLocacaoModal({
  isOpen,
  onClose,
  nomeLocatario,
  placa,
  marca,
  modelo,
  codigoCliente,
  empresaNome,
  vencidosQuantidade,
  vencidosValor,
  cartaoLocacao,
}: ClienteVirtualLocacaoModalProps) {
  if (!isOpen) return null;

  const cancelada = locacaoCancelada(cartaoLocacao ?? null);
  const inicio = cartaoLocacao?.dataInicioContrato ?? null;
  const meses =
    inicio && cancelada && cartaoLocacao?.dataCancelamento
      ? mesesContratoEntre(inicio, cartaoLocacao.dataCancelamento)
      : inicio && cancelada
        ? null
        : inicio
          ? mesesContratoEntre(inicio, new Date())
          : null;
  const dataCancelFmt =
    cancelada && cartaoLocacao?.dataCancelamento ? formatDate(cartaoLocacao.dataCancelamento) : null;
  const temPendencias = vencidosQuantidade > 0;
  const locacaoNumero = cartaoLocacao?.idLocacao?.trim() || null;
  const descricaoModelo = textoModeloVeiculo(cartaoLocacao, marca, modelo);
  const corVeiculo = cartaoLocacao?.corEquipamento?.trim() || null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        aria-label="Fechar"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md rounded-2xl border border-amber-500/30 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 text-white shadow-2xl overflow-hidden">
        <Bike className="pointer-events-none absolute -right-4 bottom-8 h-40 w-40 text-amber-500/[0.08]" aria-hidden />
        <div className="relative z-10 p-6">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-sky-300 font-bold text-lg">Zeh Motoca</p>
              <p className="text-xs text-zinc-400">Cartão virtual do locatário</p>
            </div>
            <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-white hover:bg-white/10" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="h-px bg-zinc-700 mb-4" />

          <p className="text-xs text-zinc-400 uppercase tracking-wide">Nome completo</p>
          <p className="text-lg font-semibold mb-3">{nomeLocatario || '—'}</p>

          <p className="text-xs text-zinc-400 uppercase tracking-wide">Placa</p>
          <p className="text-xl font-bold text-sky-200 mb-3">{formatPlaca(placa) || '—'}</p>

          <p className="text-xs text-zinc-400 uppercase tracking-wide">Modelo</p>
          <p className="text-base mb-1">{descricaoModelo}</p>
          {corVeiculo ? (
            <>
              <p className="text-xs text-zinc-400 uppercase tracking-wide mt-2">Cor</p>
              <p className="text-base mb-3">{corVeiculo}</p>
            </>
          ) : null}

          <p className="text-xs text-zinc-400 uppercase tracking-wide">Nº da locação</p>
          <p className="text-base mb-4">{locacaoNumero ?? '—'}</p>

          <div className="rounded-xl border border-zinc-700 bg-black/30 p-3 flex gap-3 mb-4">
            {temPendencias ? (
              <AlertCircle className="h-6 w-6 text-red-400 shrink-0" />
            ) : (
              <CheckCircle className="h-6 w-6 text-emerald-400 shrink-0" />
            )}
            <div>
              <p className="text-sm font-semibold">Pendências financeiras vencidas</p>
              <p className="text-xs text-zinc-300 mt-0.5">
                {temPendencias
                  ? `Sim — ${vencidosQuantidade} título(s) · ${formatCurrency(vencidosValor)}`
                  : 'Não'}
              </p>
            </div>
          </div>

          {meses != null ? (
            <div className="mb-3">
              <p className="text-xs text-zinc-400 uppercase tracking-wide">Tempo de contrato</p>
              <p className="text-sm">
                {meses} {meses === 1 ? 'mês' : 'meses'}
                {cancelada ? ' (do início até o cancelamento)' : ' (calendário desde o início até hoje)'}
              </p>
            </div>
          ) : cancelada && inicio && !cartaoLocacao?.dataCancelamento ? (
            <div className="mb-3">
              <p className="text-xs text-zinc-400 uppercase tracking-wide">Tempo de contrato</p>
              <p className="text-sm text-zinc-400">
                Contrato cancelado — data de cancelamento não informada no sistema.
              </p>
            </div>
          ) : null}

          {cancelada ? (
            <div className="mb-3">
              <p className="text-xs text-zinc-400 uppercase tracking-wide">Data do cancelamento</p>
              <p className="text-sm">{dataCancelFmt ?? 'Não informada no sistema'}</p>
            </div>
          ) : null}

          {cartaoLocacao?.dataFimContratoEsperado ? (
            <div className="mb-3">
              <p className="text-xs text-zinc-400 uppercase tracking-wide">Término previsto</p>
              <p className="text-sm">{formatDate(cartaoLocacao.dataFimContratoEsperado)}</p>
            </div>
          ) : null}

          {cartaoLocacao?.statusLocacao ? (
            <div className="mb-4">
              <p className="text-xs text-zinc-400 uppercase tracking-wide">Status da locação</p>
              <p className="text-sm">{cartaoLocacao.statusLocacao}</p>
            </div>
          ) : null}

          <div className="text-xs text-zinc-500 border-t border-zinc-800 pt-3 space-y-1">
            {empresaNome ? <p>{empresaNome}</p> : null}
            {codigoCliente ? <p>Ref. cliente: {codigoCliente}</p> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
